import { estClient } from './estClient';

/**
 * 0 — visible to everyone
 * 1 — visible to admin only, hide from managers
 * 2 — archived
 * 9 — permanently disabled on system level. Can not be reached by any user
 */
type HiddenFlag = '0' | '1' | '2' | '9';

/**
 * 0 — phone number
 * 1 — GetCall URL
 * 2 — Special call tracking type for Ringostat
 */
type AppealSourcePointType = '0' | '1' | '2';

export interface AppealSourceType {
  id: string;
  name: string;
  sort_order: string | null;
  is_locked: string;
  is_hidden: HiddenFlag;

  /**
   * @example '2023-12-14 12:35:11'
   */
  created_at: string;
}

export interface AppealSource {
  id: string;

  /**
   * Reference to AppealSourceType['id']
   */
  type_id: string;
  country_id: string;
  name: string;

  /**
   * Translated name to display to user
   */
  name_local: string;
  translation_key: string | null;
  sort_order: string;
  is_locked: string;
  is_hidden: HiddenFlag;

  /**
   * @example '2023-12-14 12:35:11'
   */
  created_at: string;
}

export interface AppealSourcePoint {
  id: string;

  point: string;

  point_type: AppealSourcePointType;

  /**
   * Reference to AppealSource['id']
   */
  ad_source_id: AppealSource['id'];
  object_id: string | null;
  is_skip_create_appeal: string;

  /**
   * A flag to force appeal creation for repeated requests
   */
  is_always_create_appeal: '0' | '1';
}

export interface AppealSourceListResponse {
  success: true;
  ad_source_type: AppealSourceType[];
  ad_source: AppealSource[];
  ad_source_point: AppealSourcePoint[];
}

export interface CreateAppealSourceProps {
  name: string;

  /**
   * Reference to AppealSourceType['id']
   */
  type_id: string;
  is_locked?: string;
  is_hidden?: HiddenFlag;
}

export type UpdateAppealSourceProps = Partial<
  Pick<AppealSource, 'name' | 'is_hidden'>
>;

export type CreateAppealSourcePointProps = Pick<
  AppealSourcePoint,
  'point' | 'point_type' | 'object_id' | 'ad_source_id'
>;

export type UpdateAppealSourcePointProps = Omit<
  CreateAppealSourcePointProps,
  'point_type'
> &
  Pick<AppealSourcePoint, 'is_always_create_appeal'>;

interface AppealSourceMutationSuccessResponse {
  success: true;
  message: '';
  ad_source: AppealSource;
}

interface AppealSourcePointMutationSuccessResponse {
  success: true;
  message: '';
  ad_source: AppealSourcePoint;
}

class AppealSourceApi {
  getAll(): Promise<AppealSourceListResponse> {
    return estClient.get<AppealSourceListResponse>(
      '/api/fe/ad_source/list/local',
    );
  }

  async createAppealSource({
    name,
    type_id,
    is_locked = '0',
    is_hidden = '0',
  }: CreateAppealSourceProps): Promise<void> {
    await estClient.post<AppealSourceMutationSuccessResponse>(
      '/api/db/ad_source',
      {
        body: {
          name,
          type_id,
          is_locked,
          is_hidden,
        },
      },
    );
  }

  async updateAppealSource(
    id: string,
    props: UpdateAppealSourceProps,
  ): Promise<void> {
    const entries = Object.entries(props).filter(([key]) =>
      ['name', 'is_hidden'].includes(key),
    );

    if (!entries.length) {
      throw new Error(
        `Expected non empty appeal source props for appeal source id: ${id}`,
      );
    }

    await estClient.put<AppealSourceMutationSuccessResponse>(
      `/api/db/ad_source/${id}`,
      {
        body: Object.fromEntries(entries),
      },
    );
  }

  async createAppealSourcePoint({
    point,
    point_type,
    object_id,
    ad_source_id,
  }: CreateAppealSourcePointProps): Promise<void> {
    await estClient.post<AppealSourcePointMutationSuccessResponse>(
      '/api/db/ad_source_point',
      {
        body: {
          point,
          point_type,
          object_id,
          ad_source_id,
        },
      },
    );
  }

  async updateAppealSourcePoint(
    id: string,
    {
      point,
      object_id,
      ad_source_id,
      is_always_create_appeal,
    }: UpdateAppealSourcePointProps,
  ): Promise<void> {
    await estClient.put<AppealSourcePointMutationSuccessResponse>(
      `/api/db/ad_source_point/${id}`,
      {
        body: {
          point,
          object_id,
          ad_source_id,
          is_always_create_appeal,
        },
      },
    );
  }

  async deleteAppealSourcePoint(id: string): Promise<void> {
    await estClient.delete<AppealSourcePointMutationSuccessResponse>(
      `/api/db/ad_source_point/${id}`,
    );
  }

  get POINT_TYPE() {
    return {
      PHONE: '0',
      GET_CALL: '1',

      /**
       * Special call tracking type for Ringostat
       */
      CALL_TRACKING: '2',
    } as const;
  }

  get VISIBILITY() {
    return {
      VISIBLE: '0',
      ADMIN_ONLY: '1',
      ARCHIVED: '2',
      HIDDEN: '9',
    } as const;
  }
}

export const appealSourceApi = new AppealSourceApi();
